import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { eventInfo } from '../data';
import {
  FileText,
  ShieldCheck,
  CheckCircle2,
  Lock,
  Mail,
  Building2,
  Calendar,
  AlertCircle,
} from 'lucide-react';

export function LegalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'privacy' ? 'privacy' : 'terms';
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>(initialTab);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'privacy' && activeTab !== 'privacy') {
      setActiveTab('privacy');
    } else if (tabParam !== 'privacy' && activeTab !== 'terms') {
      setActiveTab('terms');
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'terms' | 'privacy') => {
    setActiveTab(tab);
    setSearchParams(tab === 'privacy' ? { tab: 'privacy' } : {});
  };

  return (
    <div className="space-y-12">
      {/* Page Header */}
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A3D27]/10 border border-[#0A3D27]/20 text-[#0A3D27] text-xs font-bold">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Legal & Compliance Hub • {eventInfo.title}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
          Terms & Conditions and Privacy Policy
        </h1>
        <p className="text-sm text-slate-600 max-w-3xl leading-relaxed">
          Official regulatory guidelines, visitor ingress regulations, stall handover conditions,
          and cryptographic data telemetry protocols for {eventInfo.organizer.legalEntity}.
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="flex bg-slate-100 p-1.5 rounded-2xl border border-slate-200 max-w-md shadow-xs">
        <button
          onClick={() => handleTabChange('terms')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'terms'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Terms & Conditions</span>
        </button>

        <button
          onClick={() => handleTabChange('privacy')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold transition-all flex items-center justify-center gap-2 cursor-pointer ${
            activeTab === 'privacy'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <ShieldCheck className="w-4 h-4" />
          <span>Privacy Policy</span>
        </button>
      </div>

      {/* Legal Content Card */}
      <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-10 shadow-xs space-y-8 text-xs text-slate-700 leading-relaxed">
        {activeTab === 'terms' ? (
          <div className="space-y-8">
            <div className="p-5 bg-[#FAF5EA] border border-[#E6C687] rounded-2xl space-y-2">
              <span className="text-[11px] font-bold text-[#785613] uppercase tracking-wider block">
                Official Event Governance Notice
              </span>
              <p className="text-slate-800">
                These terms govern attendee registrations, delegate admissions, stall space allocations, and
                commercial exhibits for the IIAB Conference & Expo ({eventInfo.edition}) organized by{' '}
                <strong className="text-slate-900">{eventInfo.organizer.legalEntity}</strong>. By reserving a stall,
                booking a visitor pass, or entering Jio World Convention Centre (JWCC), you agree to comply with
                the following binding terms.
              </p>
            </div>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                1. Registration, Badging &amp; Turnstile Admittance
              </h3>
              <p>
                Each registered trade visitor, conference delegate, and exhibitor is issued a unique 6-character Desk
                Collection Code and a physical 4&quot; × 6&quot; printed badge bearing a cryptographic hash token (
                <code className="text-[#0A3D27] bg-[#ECFDF5] border border-emerald-200 px-1 py-0.5 rounded font-mono font-bold">
                  IIAB27-...
                </code>
                ) and NFC credential. Presentation of your printed physical badge is mandatory for ingress across venue
                turnstiles at Pavilion 3, JWCC.
              </p>
              <p>
                Badges are non-transferable and strictly personalized to the registered identity. Duplicate passes
                necessitate verification at the Secretariat Helpdesk with valid corporate identification and photo ID.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                2. Stall Allocation & Handover Rules
              </h3>
              <p>
                Stall holders must adhere strictly to municipal and venue handover time limits:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="p-3 bg-[#F8F9F6] rounded-xl border border-slate-200">
                  <strong className="text-slate-900 block">Raw Stalls (Bare Space):</strong>
                  <span>Handover at {eventInfo.stallHandoverRules.rawStallHandover}</span>
                </div>
                <div className="p-3 bg-[#F8F9F6] rounded-xl border border-slate-200">
                  <strong className="text-slate-900 block">Prefabricated Shell Scheme:</strong>
                  <span>Handover at {eventInfo.stallHandoverRules.prefabShellHandover}</span>
                </div>
              </div>
              <p className="pt-1">
                No flammable substances, open flames, heavy machinery surpassing floor load thresholds, or
                non-registered toxic agricultural active ingredients are permitted inside exhibition halls.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                3. Commercial Invoicing, Taxes & Currency Invoicing
              </h3>
              <p>
                All domestic Indian INR transactions are subject to 18% Goods and Services Tax (GST) as mandated
                by the Central Board of Indirect Taxes and Customs (CBIC) under GSTIN{' '}
                <strong className="text-slate-900 font-mono">{eventInfo.organizer.gstin}</strong>.
              </p>
              <p>
                Invoicing in USD ($) applies exclusively to overseas corporate entities remitting payment via
                international wire transfer, qualifying as an export of service under Indian foreign exchange
                regulations.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                4. Cancellation, Transfers & Force Majeure
              </h3>
              <p>
                All confirmed booth reservations and paid delegate registrations are firm and non-refundable.
                Organizers reserve the right to alter floorplan allocations to satisfy municipal fire safety and
                turnstile crowd management prerequisites.
              </p>
            </section>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="p-5 bg-[#FAF5EA] border border-[#E6C687] rounded-2xl space-y-2">
              <span className="text-[11px] font-bold text-[#785613] uppercase tracking-wider block">
                Data Protection & Privacy Commitment
              </span>
              <p className="text-slate-800">
                This Privacy Policy outlines how <strong className="text-slate-900">{eventInfo.organizer.legalEntity}</strong>{' '}
                and the IIAB 2027 Platform collect, store, and process personal and commercial information
                gathered during attendee registration and exhibitor reservations.
              </p>
            </div>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                1. Information We Collect
              </h3>
              <p>
                When registering as an attendee, exhibitor, or delegate, we collect contact credentials including
                full legal name, enterprise email address, mobile phone number, organization name, designation,
                and corporate GSTIN tax identifier (if applicable).
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                2. QR Badge Generation & Turnstile Telemetry
              </h3>
              <p>
                Each registered attendee is assigned a unique cryptographic token (e.g.,{' '}
                <code className="text-[#0A3D27] bg-[#ECFDF5] border border-emerald-200 px-1 py-0.5 rounded font-mono font-bold">IIAB27-UUID</code>) encoded into a secure 2D QR code.
                Turnstile scanner devices record timestamped entry telemetry to calculate real-time hall occupancy,
                satisfy municipal emergency guidelines, and prevent pass duplication.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                3. Payment Gateway Security (PCI-DSS)
              </h3>
              <p>
                Financial transactions for stall bookings and delegate passes are processed directly via
                PCI-DSS Level 1 compliant payment gateways (Razorpay). Neither {eventInfo.organizer.legalEntity}{' '}
                nor the IIAB 2027 Platform captures or retains credit card numbers, CVVs, or net-banking PINs on
                our infrastructure.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#0A3D27]" />
                4. Data Subject Rights & Inquiries
              </h3>
              <p>
                You retain the right to inspect, update, or request anonymization of your contact information
                post-event by submitting a request to our data protection officer at{' '}
                <a href={`mailto:${eventInfo.organizer.supportEmail}`} className="text-[#0A3D27] font-semibold underline">
                  {eventInfo.organizer.supportEmail}
                </a>
                .
              </p>
            </section>
          </div>
        )}

        {/* Legal Signoff Footer */}
        <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-slate-500">
          <div>
            Registered Office: {eventInfo.organizer.address} • GSTIN: {eventInfo.organizer.gstin}
          </div>
          <div>
            Support Inquiries:{' '}
            <a href={`mailto:${eventInfo.organizer.contactEmail}`} className="text-[#0A3D27] font-semibold underline">
              {eventInfo.organizer.contactEmail}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LegalPage;
